import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ParentDisclosureService } from '../../services/parent-disclosure.service';
import { FamilyKidsService } from '../../../parent-family/services/family-kids.service';
import { ParentEmptyStateComponent } from '../../../../shared/components/ui/parent-empty-state/parent-empty-state.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { InfoBannerComponent } from '../../../../shared/components/ui/info-banner/info-banner.component';
import {
  formatDisclosureExpiry,
  formatDisclosurePartnerRef,
  resolveDisclosureScopeLabel,
} from '../../helpers/disclosure-scope.helper';
import { DisclosureGrant } from '../../models/disclosure.model';
import { getValvurApiError } from '../../../../core/errors/parse-valvur-api-error';

@Component({
  selector: 'app-parent-disclosure-grants',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    TranslatePipe,
    ParentEmptyStateComponent,
    ParentButtonComponent,
    InfoBannerComponent,
  ],
  templateUrl: './parent-disclosure-grants.component.html',
  styleUrl: './parent-disclosure-grants.component.scss',
})
export class ParentDisclosureGrantsComponent {
  #disclosure = inject(ParentDisclosureService);
  #familyKids = inject(FamilyKidsService);
  #translate = inject(TranslateService);
  #router = inject(Router);

  readonly actingGrantId = signal<string | null>(null);
  readonly pageError = signal<string | null>(null);

  readonly grants = computed(() => this.#disclosure.activeGrants());
  readonly isLoading = computed(
    () => this.#disclosure.parentGrantsQuery.isPending(),
  );
  readonly showLoadError = computed(
    () => this.#disclosure.parentGrantsQuery.isError(),
  );

  kidName(subjectUserId: string | undefined): string {
    if (!subjectUserId) {
      return '';
    }
    const child = this.#familyKids.childById(subjectUserId);
    return child ? this.#familyKids.displayName(child) : '';
  }

  partnerLabel(partnerRef: string | undefined): string {
    return formatDisclosurePartnerRef(partnerRef ?? '');
  }

  scopeLabel(scopeId: string): string {
    return resolveDisclosureScopeLabel(scopeId, (key) =>
      this.#translate.instant(key),
    );
  }

  expiryDate(grant: DisclosureGrant): Date | null {
    return formatDisclosureExpiry(grant.expiresAt);
  }

  grantedDate(grant: DisclosureGrant): Date | null {
    return formatDisclosureExpiry(grant.grantedAt);
  }

  isActing(grantId: string): boolean {
    return this.actingGrantId() === grantId;
  }

  async refresh(): Promise<void> {
    this.pageError.set(null);
    await this.#disclosure.reloadParentGrants();
  }

  goBack(): void {
    void this.#router.navigate(['/identity']);
  }

  async revoke(grant: DisclosureGrant): Promise<void> {
    if (!grant.id) {
      return;
    }

    const confirmed = globalThis.confirm?.(
      this.#translate.instant('app.identity.disclosure.grants.revokeConfirm'),
    );
    if (confirmed === false) {
      return;
    }

    this.pageError.set(null);
    this.actingGrantId.set(grant.id);
    try {
      await this.#disclosure.revokeMutation.mutateAsync(grant.id);
    } catch (error) {
      this.pageError.set(
        getValvurApiError(error)?.message ??
          this.#translate.instant('app.identity.disclosure.page.actionError'),
      );
    } finally {
      this.actingGrantId.set(null);
    }
  }
}
